'use strict'
import React, { useState, useCallback, useMemo } from 'react'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { getMessage } from '../../../../common/utils/MessageUtils'

/*==================================
 定数定義
 ==================================*/

/*==================================
 カスタムフック
 ==================================*/
/**
 * 選択された年度ごとに、各雇用区分に対するジェンダー統計区分の選択状態を管理するフック。
 *
 * @param {string | number} selectedFiscalYear - 現在選択中の年度
 * @returns {{
 *   selectedGenderTypes: Record<string, string> // 雇用区分キー → ジェンダー統計区分の選択値
 *   handleChangeGenderType: (employmentType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => void // 選択値変更ハンドラ
 * }}
 */
export const useGenderTypeSelectionByFiscalYear = (selectedFiscalYear) => {
  // 年度ごとの選択状態をオブジェクトで管理
  const [selectedGenderTypesByYear, setSelectedGenderTypesByYear] = useState({})

  // 現在選択年度の雇用区分ごとの選択値（なければ空オブジェクト）
  const selectedGenderTypes = selectedFiscalYear ? selectedGenderTypesByYear[selectedFiscalYear] || {} : {}

  /**
   * 雇用区分に対する選択値変更ハンドラの生成
   *
   * @param {string} employmentType - 雇用区分の識別子
   * @returns {function} - イベントハンドラ関数
   */
  const handleChangeGenderType = useCallback(
    (employmentType) => (e) => {
      const selectedValue = e.target.value
      if (!selectedFiscalYear) return // selectedFiscalYear が未設定なら処理しない

      // 前の状態をコピーしつつ、該当年度・雇用区分の選択値を更新
      setSelectedGenderTypesByYear((prev) => {
        const prevForYear = prev[selectedFiscalYear] || {}
        return {
          ...prev,
          [selectedFiscalYear]: {
            ...prevForYear,
            [employmentType]: selectedValue
          }
        }
      })
    },
    [selectedFiscalYear]
  )

  return {
    selectedGenderTypes,
    selectedGenderTypesByYear,
    setSelectedGenderTypesByYear,
    handleChangeGenderType
  }
}

/*==================================
    コンポーネント（JSX）
  ===================================*/
/**
 * 雇用区分ごとのジェンダー統計区分設定テーブルコンポーネント。
 *
 * @param {Object} props
 * @param {Object[]} props.employmentTypes - 雇用区分の配列（各要素は雇用区分情報オブジェクト）
 * @param {Object} props.genderStatisticalClassificationInfo - ジェンダー統計区分情報オブジェクト（`Config`配列を含む）
 * @param {Object} props.selectedConfig - 選択年度に応じた会社設定レコード
 * @param {Record<string, string>} props.selectedGenderTypes - 雇用区分ごとの選択済みジェンダー統計区分コードマップ
 * @param {(employmentType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => void} props.handleChangeGenderType
 *   - 選択値変更イベントハンドラ
 *
 * @param {string} [props.testId=''] - テスト用IDの接頭辞（オプション）. Default is `''`
 * @param {Object} [props.inputError={}] - 入力エラー情報マップ（雇用区分キーを持つオブジェクト）. Default is `{}`
 * @returns {React.ReactElement}
 */
const EmploymentTypeSettingTable = React.memo(
  ({
    employmentTypes,
    genderStatisticalClassificationInfo,
    selectedConfig,
    selectedGenderTypes,
    handleChangeGenderType,
    testId = '',
    inputError = {}
  }) => {
    const SelectClassList = ['w-full']
    const SelectClasses = SelectClassList.join(' ')

    // 表示順で雇用区分を保持
    const sortedEmploymentTypes = useMemo(() => {
      return [...employmentTypes].sort((a, b) => {
        // displayOrderが数値として比較できるように変換
        const orderA = Number(a.displayOrder) || 0
        const orderB = Number(b.displayOrder) || 0
        return orderA - orderB
      })
    }, [employmentTypes])

    // ジェンダー統計区分の選択肢作成（カスタムラベルがある場合はそれを優先）
    const secGenderTypeOptions =
      genderStatisticalClassificationInfo?.Config?.filter(({ Code }) => Code !== 'Gender-001').map(
        ({ Code, Value }) => {
          if (!selectedConfig) return { value: Code, label: Value }

          switch (Code) {
            case 'Gender-002':
              return { value: Code, label: selectedConfig.GenderStatsRegular || Value }
            case 'Gender-003':
              return { value: Code, label: selectedConfig.GenderStatsNonRegular || Value }
            default:
              return { value: Code, label: Value }
          }
        }
      ) || []

    // 雇用区分が未登録の場合の表示
    if (!employmentTypes || employmentTypes.length === 0) {
      return (
        <div className="text-center my-10">
          <span className="text-h5 red--text">雇用区分が未登録です。</span>
        </div>
      )
    }

    const hasError = !!Object.keys(inputError?.gender || {}).length

    return (
      <div className="setting-table min-w-[100px] max-w-[50%]" data-testid={testId}>
        <table className="base-table table-fixed w-full">
          <colgroup>
            <col className="w-[20%]" />
            <col className="w-[20%]" />
          </colgroup>
          <thead>
            <tr>
              <th>雇用区分</th>
              <th>ジェンダー統計区分設定</th>
            </tr>
          </thead>
          <tbody className="text-caption-2 !text-base font-medium">
            {sortedEmploymentTypes.map((empType) => (
              <tr key={empType.employmentType} data-testid={testId ? `${testId}-${empType.employmentType}` : undefined}>
                <td>
                  <CustomTooltip title={empType.employmentType} useParentWidth={true}>
                    {(ref) => (
                      <div ref={ref} className="truncate font-normal">
                        <span>{empType.employmentType}</span>
                      </div>
                    )}
                  </CustomTooltip>
                </td>
                <td className="form-control !py-2">
                  <ChrofyCustomSelect
                    id={`gender-type-${empType.employmentType}`}
                    name={`selectedGenderType_${empType.employmentType}`}
                    value={selectedGenderTypes[empType.employmentType] || ''}
                    onChange={handleChangeGenderType(empType.employmentType)}
                    className={`${SelectClasses}`}
                    options={secGenderTypeOptions}
                    disabled={false}
                    isError={inputError?.gender?.[empType.employmentType]}
                    hasNoSelect={false}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {hasError && <p className="red--text">{getMessage('E010001', 'ジェンダー統計区分')}</p>}
      </div>
    )
  }
)

/**
 * 雇用区分に関する設定セクション全体を管理・表示するコンポーネント。
 *
 * @param {Object} props
 * @param {Object[]} props.employmentType - 雇用区分の配列
 * @param {Object} props.genderStatisticalClassificationInfo - ジェンダー統計区分情報
 * @param {Object} props.selectedConfig - 選択年度に応じた会社設定レコード
 * @param {Record<string, string>} props.selectedGenderTypes - 雇用区分ごとの選択済みジェンダー統計区分コードマップ
 * @param {(employmentType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => void} props.handleChangeGenderType
 *   - 選択値変更イベントハンドラ
 *
 * @param {Object} [props.inputError] - 入力エラー情報マップ
 * @returns {React.ReactElement}
 */
const EmploymentCategorySettings = ({
  employmentType,
  genderStatisticalClassificationInfo,
  selectedConfig,
  selectedGenderTypes,
  handleChangeGenderType,
  inputError
}) => {
  return (
    <>
      <div className="text-body-2-bold grow mb-2 ">■雇用区分に関する設定</div>
      <div className="flex items-center justify-between mb-6">
        各雇用区分について、有価証券報告書記載指標の計算時にどのカテゴリとして扱うかを設定してください。
      </div>
      <EmploymentTypeSettingTable
        employmentTypes={employmentType}
        genderStatisticalClassificationInfo={genderStatisticalClassificationInfo}
        selectedConfig={selectedConfig}
        selectedGenderTypes={selectedGenderTypes}
        handleChangeGenderType={handleChangeGenderType}
        inputError={inputError}
      />
    </>
  )
}

export default EmploymentCategorySettings
